import { message, Modal } from 'antd';
import { store } from '../../redux/utils/store';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Typography, Input, Button, Tooltip, Select } from 'antd';
import ExpansionManagement from '../../components/ExpansionManagement';
import { getOrganization, updateOrganization, pollUntilExpandedPeriodsReady } from '../../redux/actions/organizationAction';
import { periodsActions, businessActions } from '../../redux/actions/';
import Localization from '../../utils/localization';
import RenderLoader from '../../components/RenderLoader';
import RenderUpdater from '../../components/RenderUpdater';
import { allowedStartMonths, boundaryYears } from '../../shared/formula/periodStatus';

const { Title } = Typography;
const { Content } = Layout;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function detectExpandedPeriodsChangeType(oldExpanded, newExpanded) {
    const result = { type: null, changedIndexes: [] };

    if (oldExpanded === newExpanded) return result;

    const oldArr = oldExpanded.split('');
    const newArr = newExpanded.split('');

    // Detect append or remove at end
    if (newArr.length === oldArr.length + 1 && newArr.slice(0, oldArr.length).join('') === oldExpanded) {
        return { type: 'expandedPeriods:append', changedIndexes: [newArr.length - 1] };
    }
    
    // console.log('newArr:', newArr, 'oldArr:', oldArr);
    

    if (newArr.length === oldArr.length - 1 && oldArr.slice(0, newArr.length).join('') === newExpanded) {
        return { type: 'expandedPeriods:remove', changedIndexes: [oldArr.length - 1] };
    }

    // Detect single period type change (e.g., Y → M)
    let changes = 0;
    let changeType = '';
    const changedIndexes = [];

    for (let i = 0; i < Math.min(oldArr.length, newArr.length); i++) {
        if (oldArr[i] !== newArr[i]) {
            changes++;
            changedIndexes.push(i);

            if (oldArr[i] === 'Y' && (newArr[i] === 'M' || newArr[i] === 'Q')) {
                changeType = 'expandedPeriods:add';
            } else if ((oldArr[i] === 'M' || oldArr[i] === 'Q') && newArr[i] === 'Y') {
                changeType = 'expandedPeriods:delete';
            } else {
                changeType = 'expandedPeriods:reset';
            }
        }
    }

    if (changes === 1) {
        return { type: changeType, changedIndexes };
    }

    return { type: null, changedIndexes };
}

const PeriodManagement = () => {
    const dispatch = useDispatch();
    const organization = useSelector((state) => state.organization.organization);
    const periods = useSelector((state) => state.periods.periods);
    const expandedPeriods = useSelector((state) => state.periods.expandedPeriods);
    const updatingOrganization = useSelector(state => state.organization.updatingOrganization);
    const pollingExpandedPeriods = useSelector(state => state.organization.pollingExpandedPeriods);
    const fetchingExpandedPeriods = useSelector((state) => state.periods.fetchingExpandedPeriods);

    const [startingYearInput, setStartingYearInput] = useState(null);
    const [actualsInput, setActualsInput] = useState(null);
    const [budgetInput, setBudgetInput] = useState(null);
    const [forecastInput, setForecastInput] = useState(null);
    const [isExpansionManagementOpen, setIsExpansionManagementOpen] = useState(false);
    const [highlightedYears, setHighlightedYears] = useState([]);
    
    
    
    useEffect(() => {
        dispatch(getOrganization());
    }, [dispatch]);

    useEffect(() => {
        if (organization != null && periods == null && expandedPeriods == null) {
            dispatch(periodsActions.getPeriods());
            if (!fetchingExpandedPeriods) dispatch(periodsActions.getExpandedPeriods());
        }
    }, [dispatch, organization, periods, fetchingExpandedPeriods, expandedPeriods]);

    useEffect(() => {
        if (organization) {
           // console.log('ORGANIZATION CHANGED:', organization);
            setStartingYearInput(+organization.starting_year);
            setActualsInput(+organization.actuals);
            setBudgetInput(+organization.budget);
            setForecastInput(+organization.forecast);
        }
    }, [organization]);

    // Actuals/budget/forecast are saved in the request itself (no background job),
    // so there is nothing to poll for. Returns the updated organization, or undefined on failure.
    const updateOrganizationAndReload = async (value, type) => {
        if (updatingOrganization) return undefined;
        return dispatch(updateOrganization(organization, organization.expanded_periods, value, type));
    };

    /**
     * Recalculates values to maintain period total, adjusting forecast by default.
     * @param {'actuals' | 'budget' | 'forecast'} changedField 
     * @param {number} newValue 
     * @returns {[string, number][]} - List of [fieldName, newValue] pairs to update
     */

    const computeBalancedFields = (changedField, newValue) => {
       const oldValues = {
                actuals: +organization.actuals,
                budget: +organization.budget,
                forecast: +organization.forecast
            };
        
        const totalPeriods = organization?.expanded_periods?.length ?? (
        oldValues.actuals + oldValues.budget + oldValues.forecast
    );
        const numericValue = Number(newValue);
        const delta = numericValue - oldValues[changedField];
        const updates = [[changedField, numericValue]];

        switch (changedField) {
            case 'actuals':
            case 'budget': {
                const newForecast = oldValues.forecast - delta;
                const total = changedField === 'actuals'
                    ? numericValue + oldValues.budget + newForecast
                    : oldValues.actuals + numericValue + newForecast;
                
                if (newForecast < 0 || total !== totalPeriods) {
                    throw new Error(`Forecast would become negative or total mismatch.`);
                }
                updates.push(['forecast', newForecast]);
                break;
            }

            case 'forecast': {
                const newActuals = oldValues.actuals - delta;
                const total = newActuals + oldValues.budget + numericValue;

                if (newActuals < 0 || total !== totalPeriods) {
                    throw new Error(`Actuals would become negative or total mismatch.`);
                }
                updates.push(['actuals', newActuals]);
                break;
            }

            default:
                throw new Error(`Unknown field: ${changedField}`);
        }

        return updates;
    };

    const handleUpdateField = async (field, newValue) => {
        if (updatingOrganization) return;

        try {
            const updates = computeBalancedFields(field, newValue);

            for (const [key, val] of updates) {
                if (key === 'actuals') setActualsInput(val);
                if (key === 'budget') setBudgetInput(val);
                if (key === 'forecast') setForecastInput(val);
                const updated = await updateOrganizationAndReload(val, key);
                if (!updated) {
                    message.error(`Could not update ${key}.`);
                    await dispatch(getOrganization());
                    return;
                }
            }
            // Colors, periods and formulas such as ISFORECAST() depend on it.
            window.location.reload();
        } catch (err) {
            console.error(err);
            message.error(err.message);
        }
    };


//    const handleUpdateStartingYear = async () => {
//        await updateOrganizationAndReload(startingYearInput, 'starting_year');
//    };

    const handleUpdateStartMonth = async (field, month) => {
        if (updatingOrganization) return;
        const updated = await dispatch(
            updateOrganization(organization, organization.expanded_periods, { [field]: month }, 'periodStartMonths')
        );
        if (!updated) {
            message.error('Could not change the start month. The budget must start before the forecast.');
            return;
        }
        // Colors and formulas such as ISFORECAST() depend on it; reload like other period changes.
        window.location.reload();
    };

    /** Year in which budget/forecast starts, and the month picker for it (null when outside the periods). */
    const renderStartMonth = (label, field, year) => {
        const index = year - organization.starting_year;
        if (index < 0 || index >= (organization.expanded_periods?.length ?? 0)) return null;

        const months = allowedStartMonths(organization.expanded_periods[index]);
        const value = organization[field] ?? 1;

        return (
            <Title level={5} style={{ textAlign: 'left', marginLeft: '40px' }}>
                {label}
                <Tooltip
                    title={months.length > 1 ? Localization.settings.startMonthHint : Localization.settings.startMonthNeedsExpansion}
                >
                    <Select
                        value={value}
                        disabled={months.length === 1 && value === 1}
                        onChange={(month) => handleUpdateStartMonth(field, month)}
                        style={{ width: '80px', marginLeft: '40px' }}
                        options={months.map((m) => ({ value: m, label: MONTH_NAMES[m - 1] }))}
                    />
                </Tooltip>
                <span style={{ marginLeft: '12px', fontWeight: 'normal' }}>{year}</span>
            </Title>
        );
    };

    const handleUpdateActuals = () => handleUpdateField('actuals', actualsInput);
    const handleUpdateBudget = () => handleUpdateField('budget', budgetInput);
    const handleUpdateForecast = () => handleUpdateField('forecast', forecastInput);
    
    const handleUpdateExpandedPeriods = async (newExpandedPeriods) => {
        if (updatingOrganization) return;
        const { type, changedIndexes } = detectExpandedPeriodsChangeType(
            organization.expanded_periods,
            newExpandedPeriods
        );
        
        if (!type) {
            const changedYears = changedIndexes.map(index => organization.starting_year + index);
            setHighlightedYears(changedIndexes);
            setTimeout(() => setHighlightedYears([]), 2000);
            message.error(`Please only change one year at a time: ${changedYears.join(', ')}`);
            return;
        }

        const oldExpandedPeriods = organization.expanded_periods;
        
        await dispatch(
            updateOrganization(
            organization,
            newExpandedPeriods,
            0,
            type,
            oldExpandedPeriods
            )
        );

        if (oldExpandedPeriods !== newExpandedPeriods) {
            dispatch(businessActions.clearAssembledBusinessCases());
            }
        
        await dispatch(getOrganization());
        const updatedOrganization = store.getState().organization.organization;
        
        if (updatedOrganization?.organization_id) {
            await dispatch(pollUntilExpandedPeriodsReady(updatedOrganization.organization_id));
        }
    };
    
    const handleAppendOrRemoveYear = async (actionType) => {
        if (updatingOrganization) return;
        
        const current = organization.expanded_periods;
  /* We could use lastChar and firstChar to determine if we are appending or prepending, but 
  for now it is a hardcode 'Y' because expanded to months is uncommon and if the user wants 
  they can expand after adding a year */

    const lastChar = current[current.length - 1];
    const firstChar = current[0];

    let newExpandedPeriods = '';
    let type = '';

    switch (actionType) {
        case 'append':
        newExpandedPeriods = current + 'Y'; 
        type = 'expandedPeriods:append';
        break;
        case 'prepend':
        newExpandedPeriods = 'Y' + current;
        type = 'expandedPeriods:prepend';
        break;
        case 'removeEnd':
        if (current.length <= 1) {
            message.error('Cannot remove the last year.');
            return;
        }
        newExpandedPeriods = current.slice(0, -1);
        type = 'expandedPeriods:removeEnd';
        break;
        case 'removeStart':
        if (current.length <= 1) {
            message.error('Cannot remove the last year.');
            return;
        }
        newExpandedPeriods = current.slice(1);
        type = 'expandedPeriods:removeStart';
        break;
        default:
        message.error('Unknown action.');
        return;
    }
 
    let value = 0;
    let targetYear = 0;

    const { starting_year, actuals, budget, forecast } = organization;

    if (type === 'expandedPeriods:append') {
    value = forecast + 1;
    targetYear = starting_year + actuals + budget + forecast;
    } else if (type === 'expandedPeriods:prepend') {
    value = actuals + 1;
    targetYear = starting_year - 1;
    } else if (type === 'expandedPeriods:removeEnd') {
    value = forecast - 1;
    targetYear = starting_year + actuals + budget + forecast - 1;
    } else if (type === 'expandedPeriods:removeStart') {
    value = actuals - 1;
    targetYear = starting_year;
    }

    const oldExpandedPeriods = organization.expanded_periods; // Capture before mutation
  //  console.log('Old expanded periods:', oldExpandedPeriods);
    await dispatch(
        updateOrganization(
            {
            ...organization,
            ...(type === 'expandedPeriods:prepend' && {
                starting_year: organization.starting_year - 1,
            }),
            ...(type === 'expandedPeriods:removeStart' && {
                starting_year: organization.starting_year + 1,
            }),
            },
            newExpandedPeriods,
            value,
            type,
            oldExpandedPeriods,
            targetYear
        )
    );

    if (oldExpandedPeriods !== newExpandedPeriods) {
        dispatch(businessActions.clearAssembledBusinessCases());
        }

    await dispatch(pollUntilExpandedPeriodsReady(organization.organization_id));
};

const handleRemoveYear = (actionType) => {
  const positionText = actionType === 'removeEnd' ? 'the end' : 'the start';

  Modal.confirm({
    title: 'Confirm Removal',
    content: `Are you sure you want to remove a year from ${positionText}? This action cannot be undone.`,
    okText: 'Yes, remove',
    okType: 'danger',
    cancelText: 'Cancel',
    onOk: () => handleAppendOrRemoveYear(actionType),
  });
};

const handleAddYear = (actionType) => {
  const isPrepend = actionType === 'prepend';
  const positionText = isPrepend ? 'the beginning' : 'the end';

  Modal.confirm({
    title: `Confirm Addition`,
    content: `Add a year at ${positionText}?`,
    okText: 'Yes, add',
    cancelText: 'Cancel',
    onOk: () => handleAppendOrRemoveYear(actionType),
  });
};

    const renderContent = () => {
        if (!organization) {
            return <RenderLoader />;
        } else if (updatingOrganization || pollingExpandedPeriods) {
            return <RenderUpdater />;
        } else {
            return (
                <Layout style={{ height: '100%', marginTop: '0px' }}>
                    <Content style={{ margin: '16px 16px 0' }}>
                        <div style={{ padding: 24, width: '95%', margin: '0 auto' }}>
                            <Title level={3} style={{ textAlign: 'left', margin: 0, marginTop: 30 }}>
                                {Localization.settings.customPeriods}
                            </Title>

                            {/* Starting Year */}
                            <Title level={5} style={{ textAlign: 'left', marginLeft: '40px' }}>
                                {Localization.settings.startingYear}
                                <span style={{ marginLeft: '67px', fontWeight: 'normal' }}>
                                   {startingYearInput}
                                </span>
                            </Title>

                            {/* Actuals */}
                            <Title level={5} style={{ textAlign: 'left', marginLeft: '40px' }}>
                                {Localization.settings.actuals}
                                <Tooltip title={Localization.settings.actualsWarning}>
                                    <Input
                                        value={actualsInput}
                                        onChange={(e) => setActualsInput(e.target.value)}
                                        onBlur={handleUpdateActuals}
                                        onPressEnter={handleUpdateActuals}
                                        style={{ textAlign: 'right', width: '80px', marginLeft: '108px' }}
                                    />
                                </Tooltip>
                            </Title>

                            {/* Budget */}
                            <Title level={5} style={{ textAlign: 'left', marginLeft: '40px' }}>
                                {Localization.settings.budget}
                                <Tooltip title={Localization.settings.budgetWarning}>
                                    <Input
                                        value={budgetInput}
                                        onChange={(e) => setBudgetInput(e.target.value)}
                                        onBlur={handleUpdateBudget}
                                        onPressEnter={handleUpdateBudget}
                                        style={{ textAlign: 'right', width: '80px', marginLeft: '112px' }}
                                    />
                                </Tooltip>
                            </Title>

                            {/* Forecast */}
                            <Title level={5} style={{ textAlign: 'left', marginLeft: '40px' }}>
                                {Localization.settings.forecast}
                                <Tooltip title={Localization.settings.forecastWarning}>
                                    <Input
                                        value={forecastInput}
                                        onChange={(e) => setForecastInput(e.target.value)}
                                        onBlur={handleUpdateForecast}
                                        onPressEnter={handleUpdateForecast}
                                        style={{ textAlign: 'right', width: '80px', marginLeft: '100px' }}
                                    />
                                </Tooltip>
                            </Title>

                            {/* Month the budget and forecast start in their first year */}
                            {renderStartMonth(Localization.settings.budgetStarts, 'budget_start_month', boundaryYears(organization).budgetStartYear)}
                            {renderStartMonth(Localization.settings.forecastStarts, 'forecast_start_month', boundaryYears(organization).forecastStartYear)}

                            {/* Number of Periods */}
                          {/*  <Title level={5} style={{ textAlign: 'left', marginLeft: '40px' }}>
                                {Localization.settings.numPeriods}
                                <Input
                                    readOnly
                                    value={(+actualsInput || 0) + (+budgetInput || 0) + (+forecastInput || 0)}
                                    style={{ textAlign: 'right', width: '80px', marginLeft: '25px' }}
                                />
                            </Title>
                            */}
                           {/* Add or Remove Period Year */}
                            <div style={{ marginTop: '30px', marginLeft: '40px' }}>
                                <Title level={5}>Add or Remove Year</Title>
                                
                                <Button
                                    type="primary"
                                    onClick={() => handleAddYear('prepend')}
                                    style={{ marginRight: '12px' }}
                                >
                                    ➕ Add Year at Start
                                </Button>
                                
                                <Button
                                    type="primary"
                                    onClick={() => handleAddYear('append')}
                                    style={{ marginRight: '12px' }}
                                >
                                    ➕ Add Year at End
                                </Button>
                                
                                <Button
                                    danger
                                    onClick={() => handleRemoveYear('removeStart')}
                                    style={{ marginRight: '12px' }}
                                >
                                    ➖ Remove Year at Start
                                </Button>
                                
                                <Button
                                    danger
                                    onClick={() => handleRemoveYear('removeEnd')}
                                >
                                    ➖ Remove Year at End
                                </Button>
                            </div>

                            <Button
                                type="primary"
                                style={{ float: 'left', marginLeft: '39px', marginTop: '25px' }}
                                onClick={() => setIsExpansionManagementOpen(true)}
                            >
                                Expansion Management
                            </Button>

                            <ExpansionManagement
                                isExpansionManagementOpen={isExpansionManagementOpen}
                                handleClose={() => setIsExpansionManagementOpen(false)}
                                organization={organization}
                                handleUpdateExpandedPeriods={handleUpdateExpandedPeriods}
                                highlightedYears={highlightedYears}
                            />
                        </div>
                    </Content>
                </Layout>
            );
        }
    };

    return renderContent();
};

export default PeriodManagement;